import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Copy, Loader2, Plus, Trash2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useT, Bi } from "@/lib/i18n";
import { inr } from "@/lib/format";
import { Field, BigInput, MoneyInput, Stepper } from "@/components/form";

type Draft = { name: string; floor: string; sharing: number; rent_per_bed: number; deposit: number };

export function nextName(name: string, i: number): string {
  const m = name.match(/^(.*?)(\d+)$/);
  if (!m) return `${name}-${i + 1}`;
  const n = String(Number(m[2]) + i);
  return m[1] + n.padStart(m[2].length, "0");
}

function RoomBuilder({ onAdd }: { onAdd: (rooms: Draft[]) => void }) {
  const { t } = useT();
  const [d, setD] = useState<Draft>({ name: "101", floor: "1", sharing: 2, rent_per_bed: 7000, deposit: 10000 });
  const [copies, setCopies] = useState(0);
  const set = (k: keyof Draft, v: any) => setD({ ...d, [k]: v });
  const add = () => {
    if (!d.name.trim()) return;
    onAdd(Array.from({ length: copies + 1 }, (_, i) => ({ ...d, name: nextName(d.name.trim(), i) })));
    setD({ ...d, name: nextName(d.name.trim(), copies + 1) });
    setCopies(0);
  };
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <Field label={t("room_no")}><BigInput data-testid="room-name-input" value={d.name} onChange={(e) => set("name", e.target.value)} /></Field>
        <Field label={t("floor")}><BigInput data-testid="room-floor-input" value={d.floor} onChange={(e) => set("floor", e.target.value)} /></Field>
      </div>
      <Field label={t("sharing")}><Stepper testId="room-sharing-stepper" value={d.sharing} onChange={(v) => set("sharing", v)} /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label={t("rent_per_bed")}><MoneyInput data-testid="room-rent-input" value={d.rent_per_bed} onChange={(v) => set("rent_per_bed", v)} /></Field>
        <Field label={t("deposit")}><MoneyInput data-testid="room-deposit-input" value={d.deposit} onChange={(v) => set("deposit", v)} /></Field>
      </div>
      <div className="rounded-xl bg-emerald-50/70 border border-emerald-200 p-3 space-y-2">
        <p className="text-sm font-semibold text-emerald-900 flex items-center gap-2"><Copy className="h-4 w-4" />{t("copy_similar")}</p>
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs text-emerald-800">{t("copy_count")}</span>
          <Stepper testId="room-copy-stepper" value={copies} min={0} max={30} onChange={setCopies} />
        </div>
        {copies > 0 && (
          <p data-testid="room-copy-preview" className="text-xs text-emerald-800 font-mono-num">
            {Array.from({ length: copies + 1 }, (_, i) => nextName(d.name.trim() || "1", i)).join(", ")}
          </p>
        )}
      </div>
      <Button data-testid="room-add-btn" type="button" onClick={add} variant="outline" className="w-full h-12 rounded-xl border-emerald-700 text-emerald-800 font-semibold">
        <Plus className="h-4 w-4 mr-1" /> {t("add_room")} {copies > 0 ? `(+${copies})` : ""}
      </Button>
    </div>
  );
}

export function AddRoomsForm({ propertyId, onDone }: { propertyId: string; onDone: () => void }) {
  const { t } = useT();
  const [rooms, setRooms] = useState<Draft[]>([]);
  const [busy, setBusy] = useState(false);
  const addRooms = (r: Draft[]) => {
    const names = new Set(rooms.map((x) => x.name));
    const fresh = r.filter((x) => !names.has(x.name));
    if (fresh.length < r.length) toast.error("Duplicate room numbers skipped");
    setRooms([...rooms, ...fresh]);
  };
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/properties/${propertyId}/rooms`, { rooms });
      toast.success(`${rooms.length} rooms created`);
      setRooms([]);
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const beds = rooms.reduce((s, r) => s + r.sharing, 0);
  return (
    <div className="space-y-5">
      <RoomBuilder onAdd={addRooms} />
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">{t("rooms_added")} · {rooms.length} / {beds} beds</p>
        {rooms.length === 0 ? (
          <p data-testid="rooms-draft-empty" className="text-sm text-slate-500 rounded-xl border border-dashed border-slate-300 p-4">{t("no_rooms_yet")}</p>
        ) : (
          <ul className="space-y-2 max-h-64 overflow-y-auto" data-testid="rooms-draft-list">
            {rooms.map((r, i) => (
              <li key={r.name} className="flex items-center justify-between rounded-xl bg-white border border-slate-200 px-3 py-2">
                <span className="text-sm"><b>{t("room")} {r.name}</b> · {r.sharing} {t("bed")} · {inr(r.rent_per_bed)}</span>
                <button data-testid={`rooms-draft-remove-${i}`} onClick={() => setRooms(rooms.filter((_, j) => j !== i))} className="h-10 w-10 grid place-items-center text-slate-400 hover:text-rose-600">
                  <Trash2 className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <Button data-testid="rooms-create-btn" disabled={!rooms.length || busy} onClick={submit} className="w-full h-14 rounded-xl text-base bg-emerald-700 hover:bg-emerald-800">
        {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Bi k="create_rooms" />}
      </Button>
    </div>
  );
}
